import React, { useCallback, useEffect, useRef, useState } from 'react';
import Form from 'devextreme-react/form';
import 'devextreme-react/autocomplete';
import 'devextreme-react/date-range-box';
import { userService } from '@/projects/admin/services/user-service';
import Validator from 'devextreme/ui/validator';
import { roleService } from '@/projects/admin/services/role-service';

import { accountService } from '@/projects/admin/services/account-service';
import { IUser } from '@/core/interfaces/admin/usestate-interfaces';
import { useNavigate, useParams } from 'react-router-dom';
import { languageService } from '@/core/services/language-service';
import { ELEMENT_ID } from '@/core/constants/common/element-constants';
import FormWrapper from '@/core/wrappers/form-wrapper';
import { BUTTON_CONSTANTS } from '@/core/constants/common/button-contants';
import CustomStore from 'devextreme/data/custom_store';
import { cultureData } from '@/core/datas/culture-data';
import { t } from 'i18next';
import clsx from 'clsx';
import DataGridWrapper from '@/core/wrappers/data-grid-wrapper';
import DataGrid, { Column, Paging, Editing, Grouping } from 'devextreme-react/data-grid';
import { createCustomStore } from '@/core/wrappers/custom-store-wrapper';

const isActiveOptions = {
    elementAttr: {
        [ELEMENT_ID]: "isActive",
    },
    value: false,
};

function UserDefinition() {
    const formRef: any = useRef<Form>();
    const [accounts, setAccounts] = useState<CustomStore>();
    const [user, setUser] = useState<IUser>({ Id: '', accountId: '', email: '', userName: '', password: '', firstName: '', lastName: '', isEnabled: false, roles: [], languageId: null, culture: '' });
    const [roles, setRoles] = useState<CustomStore | null>(null);
    const [languages, setLanguages] = useState<CustomStore>();
    const [password, setPassword] = useState('');
    const [selectedAccount, setSelectedAccount] = useState<any | null>(null);
    const [cultures, setCultures] = useState({});
    const [features, setFeatures] = useState<any>([]);
    const [tab, setTab] = useState('Main')
    const navigate = useNavigate();
    const params = useParams();
    const userId = params.id;

    const handleSubmit = useCallback(async (): Promise<void> => {
        return new Promise(async (resolve, reject) => {
            try {
                const userForm = formRef.current.props.formData;
                if (userId) {
                    if (userForm.password == undefined || userForm.password == '') delete userForm.password;
                    await userService.updateUser(null, userForm)
                } else {
                    const res = await userService.createUser(userForm)
                }
                resolve();
                navigate(`/admin/user-list`)
            } catch (error) {
                reject(error)
            }

        })
    }, []);

    useEffect(() => {
        (async function () {

            if (userId && user.Id == '') {
                const getUser = await userService.getUser(userId);
                const data = getUser.data;
                setUser({ Id: userId, accountId: data.accountId, email: data.email, userName: data.userName, password: '', firstName: data.firstName, lastName: data.lastName, isEnabled: data.isEnabled, roles: data.roles, languageId: data.languageId, culture: data.culture });
                const groupedByModule = Object.values(data.userFeatures.reduce((acc:any, item:any) => {
                    const module = item.moduleName;
                  
                    if (!acc[module]) {
                      acc[module] = { moduleName: module, items: [] };
                    }
                  
                    acc[module].items.push({
                      id: item.id,
                      featureName: item.featureName
                    });
                  
                    return acc;
                  }, {}));
                setFeatures(data.userFeatures)
            }
        })();

        setAccounts(createCustomStore({
            key: 'id',
            load: accountService.lookupAccounts,
            byKey: (key, loadOptions: any) => {
                if (key) {
                    loadOptions.filter = ["Id", "=", key];
                }
            },
        }));

        setLanguages(createCustomStore({
            key: 'id',
            load: languageService.getLanguages,
            byKey: (key, loadOptions) => {
                if (key) {
                    loadOptions.filter = ["Id", "=", key];
                }
            },
        }));

        const fetchCultures = async () => {
            setCultures(cultureData);
        };

        fetchCultures();

    }, [user])

    useEffect(() => {
        if (selectedAccount) {
            setRoles(createCustomStore({
                key: 'id',
                load: roleService.lookupRole,
                byKey: (key, loadOptions) => {
                    if (key) {
                        loadOptions.filter = ["Id", "=", key];
                    }
                },
                onLoading(loadOptions) {
                    const accountTypeFilter: any = []
                    selectedAccount.accountTypes.forEach((accountType: any) => {
                        accountTypeFilter.push("or");
                        accountTypeFilter.push(["ParameterAccountTypeId", "=", accountType]);
                    });
                    loadOptions.filter = [
                        ["AccountId", "=", selectedAccount.id],
                        ...accountTypeFilter,
                    ];
                }
            }));
        }

    }, [selectedAccount]);

    const passwordComparison = useCallback<any>(() => password, [password]);
    const changePasswordMode = useCallback((name: any) => {
        if (formRef != null && formRef.current != null) {
            const editor = formRef.current.instance.getEditor(name);
            if (editor)
                editor.option('mode', editor.option('mode') === 'text' ? 'password' : 'text');
        }
    }, []);

    const getPasswordOptions = useCallback(() => ({
        //,autocomplete="new-password"
        inputAttr: { [ELEMENT_ID]: 'password', autocomplete: 'one-time-code' },
        mode: 'password',
        valueChangeEvent: 'keyup',
        onValueChanged: (e: any) => {
            setPassword(e.value);
            if (formRef != null && formRef.current != null) {
                const editor = formRef.current.instance.getEditor('confirmPassword');
                if (editor != null && editor.option('value')) {
                    const instance = Validator.getInstance(editor.element()) as Validator;
                    instance.validate();
                }
            }
        },
        buttons: [
            {
                name: 'password',
                location: 'after',
                options: {
                    stylingMode: 'text',
                    icon: 'eyeopen',
                    onClick: () => changePasswordMode('password'),
                },
            },
        ],
    }), [changePasswordMode]);

    const getConfirmOptions = useCallback(() => ({
        inputAttr: { [ELEMENT_ID]: 'passwordConfirm', autocomplete: 'one-time-code' },
        mode: 'password',
        valueChangeEvent: 'keyup',
        buttons: [
            {
                name: 'password',
                location: 'after',
                options: {
                    stylingMode: 'text',
                    icon: 'eyeopen',
                    onClick: () => changePasswordMode('confirmPassword'),
                },
            },
        ],
    }), [changePasswordMode]);

    const accountEditorOptions = {
        inputAttr: { [ELEMENT_ID]: 'account' },
        dataSource: accounts,
        displayExpr: 'name',
        valueExpr: 'id',
        searchEnabled: true,
        onSelectionChanged: (e: any) => {
            if (!selectedAccount || selectedAccount.id !== e.selectedItem.id)
                setSelectedAccount(e.selectedItem);
        }
    };

    const firstNameEditorOptions = {
        inputAttr: { [ELEMENT_ID]: 'firstName' },
    };

    const lastNameEditorOptions = {
        inputAttr: { [ELEMENT_ID]: 'lastName' },
    };

    const emailEditorOptions = {
        inputAttr: { [ELEMENT_ID]: 'email' },
    };

    const userNameEditorOptions = {
        inputAttr: { [ELEMENT_ID]: 'userName', autocomplete: 'one-time-code' },
        disabled: userId ? true : false
    };

    const noDigitsPattern = /^[^0-9]+$/;

    const roleEditorOptions = {
        inputAttr: { [ELEMENT_ID]: 'roles' },
        dataSource: roles,
        displayExpr: "name",
        valueExpr: "id",
        searchEnabled: true

    };
    const languageEditorOptions = {
        inputAttr: { [ELEMENT_ID]: 'language' },
        dataSource: languages,
        displayExpr: 'description',
        valueExpr: 'id',
        placeholder: undefined,
    };

    const cultureEditorOptions = {
        inputAttr: { [ELEMENT_ID]: 'culture' },
        dataSource: cultures,
        displayExpr: 'culture',
        valueExpr: 'culture',
        placeholder: undefined,
    };
    const GroupTemplate = (item: any) => <div> {item.moduleName}</div>;

    function ItemTemplate(data: any) {
        return <div >{data.featureName}</div>;
    }

    return (


        <div className='card card-custom m-5'>
            <div className='card-header card-header-stretch overflow-auto'>
                <ul
                    className='nav nav-stretch nav-line-tabs fw-semibold border-transparent flex-nowrap'
                    role='tablist'
                >
                    <li className='nav-item'>
                        <a
                            className={clsx(`nav-link cursor-pointer`, { active: tab === 'Main' })}
                            onClick={() => setTab('Main')}
                            role='tab'
                        >
                            {t("definition")}
                        </a>
                    </li>
                    {userId && (<li className='nav-item'>
                        <a
                            className={clsx(`nav-link cursor-pointer`, { active: tab === 'Features' })}
                            onClick={() => setTab('Features')}
                            role='tab'
                        >
                            {t("features")}
                        </a>
                    </li>)}
                </ul>
            </div>
            <div className='card-body'>
                <div className='tab-content pt-3'>
                    <div className={clsx('tab-pane', { active: tab === 'Main' })}>
                        <FormWrapper
                            onSubmit={handleSubmit}
                            formRef={formRef}
                            formData={user}
                            backButtonNavigationPath='/admin/user-list'
                            buttonGroups={[
                                { buttonOptions: BUTTON_CONSTANTS.SaveBtn }
                            ]}
                            simpleItemGroups={[
                                {
                                    caption: user.Id == '' ? t('new_user') : t('edit_user'),
                                    useColCountByScreen: true,
                                    items: [
                                        {
                                            dataField: "firstName",
                                            editorOptions: firstNameEditorOptions,
                                            label: 'firstname',
                                            isRequired: true,
                                            requiredMessage: 'firstname',
                                            pattern: noDigitsPattern,
                                            patternMessage: 'Do not use digits in the Firstname'
                                        },
                                        {
                                            dataField: "lastName",
                                            editorOptions: lastNameEditorOptions,
                                            label: 'lastname',
                                            isRequired: true,
                                            requiredMessage: 'lastname',
                                            pattern: noDigitsPattern,
                                            patternMessage: 'Do not use digits in the Lastname'
                                        },
                                        {
                                            dataField: "email",
                                            editorType: "dxTextBox",
                                            editorOptions: emailEditorOptions,
                                            label: 'email',
                                            isRequired: true,
                                            requiredMessage: 'email',
                                            email: true,
                                            emailMessage: 'Email is invalid',
                                        },
                                        {
                                            dataField: "userName",
                                            editorOptions: userNameEditorOptions,
                                            label: 'username',
                                            isRequired: true,
                                            requiredMessage: 'username',
                                        },

                                        {
                                            dataField: "password",
                                            editorType: "dxTextBox",
                                            editorOptions: getPasswordOptions(),
                                            label: 'password',
                                            isRequired: !userId ? true : false,
                                            requiredMessage: 'password',
                                            pattern: "^(?=.*[A-Z])(?=.*\\d)(?=.*[@$!%*?&\\-_\\.])[A-Za-z\\d@$!%*?&\\-_\\.]{8,}$",
                                            patternMessage: "password_validation_message",
                                        },
                                        {
                                            dataField: "confirmPassword",
                                            editorType: "dxTextBox",
                                            editorOptions: getConfirmOptions(),
                                            label: 'confirm_password',
                                            isRequired: !userId ? true : false,
                                            requiredMessage: 'confirm_password',
                                            compare: true,
                                            compareMessage: "Password and Confirm Password do not match",
                                            comparisonTarget: passwordComparison
                                        },
                                        {
                                            dataField: "accountId",
                                            editorType: "dxSelectBox",
                                            editorOptions: accountEditorOptions,
                                            label: 'account',
                                            isRequired: true,
                                            requiredMessage: 'account',
                                        },
                                        {
                                            dataField: "roles",
                                            editorType: "dxTagBox",
                                            editorOptions: roleEditorOptions,
                                            label: 'roles',
                                        },
                                        {
                                            dataField: "languageId",
                                            editorType: "dxSelectBox",
                                            editorOptions: languageEditorOptions,
                                            label: 'language',
                                            isRequired: true,
                                            requiredMessage: 'language',
                                        },
                                        {
                                            dataField: "culture",
                                            editorType: "dxSelectBox",
                                            editorOptions: cultureEditorOptions,
                                            label: 'culture',
                                            isRequired: true,
                                            requiredMessage: 'culture',
                                        },
                                        {
                                            dataField: "isEnabled",
                                            editorType: "dxSwitch",
                                            editorOptions: isActiveOptions,
                                            label: 'user_is_active',
                                        }
                                    ]
                                }
                            ]}
                        />
                    </div>
                    {userId && (<div className={clsx('tab-pane', { active: tab === 'Features' })}>

                        <DataGrid
                            keyExpr="id"
                            height={500}
                            dataSource={features}
                            showBorders={true}
                            remoteOperations={false}

                        >
                            <Paging enabled={false} />
                            <Grouping autoExpandAll={false} />
                            <Column
                                dataField={'moduleName'}
                                caption={''}
                                allowFiltering={false}
                                allowSorting={false}
                                groupIndex={0} 
                                groupCellTemplate={(element: any, data: any) => {
                                    element.innerHTML = data.displayValue;
                                }}
                            />
                            <Column
                                dataField={'featureName'}
                                caption={t('features')}
                                allowFiltering={false}
                                allowSorting={false}
                            />
                        </DataGrid>
                    </div>)}
                </div>

            </div>

        </div>




    );
}

export default UserDefinition;
